import React, { useEffect, useState } from 'react';
import { Moon, Sun, Trash2 } from 'lucide-react';
import { PdfUploader } from '../components/PdfUploader';
import { AnnotationToolbar } from '../components/AnnotationToolbar';
import { ToolsIconRail } from '../components/ToolsIconRail';
import type { FieldToolId } from '../components/FieldToolsBar';
import { applyThemeClass, persistTheme, resolveTheme } from '../lib/theme';

export interface CommandBarProps {
  currentFileName: string;
  isBusy: boolean;
  isPlacing: boolean;
  canPlace: boolean;
  hasPdf: boolean;
  hasFields: boolean;
  canDownload: boolean;
  isSignedOutputStale: boolean;
  isDownloading?: boolean;
  onFileSelected: (file: File, sourceHandle?: FileSystemFileHandle | null) => void;
  onUploadError?: (message: string) => void;
  onCreateDrawSignature: () => void;
  onCreateTypeSignature: () => void;
  onCreateUploadSignature: () => void;
  onPlacePendingSignature: () => void;
  onApply: () => void;
  onDownload: () => void;
  onCleanup: () => void;
  onBusinessPreset: () => void;
  onSaveSnapshot?: () => void;
  panMode?: boolean;
  onTogglePan?: () => void;
  /** Toggle the right inspector slide-over drawer (only used at <768px). */
  onToggleInspector?: () => void;
  /** Whether the inspector drawer is open (controls the toggle's pressed state). */
  inspectorOpen?: boolean;
  isUploading?: boolean;
  isSigning?: boolean;
  uploadProgress?: number;
  signingProgress?: number;
  isCancelling?: boolean;
  onCancelUpload?: () => void;
  onCancelSigning?: () => void;
  fileSize?: number;
  canUndo?: boolean;
  canRedo?: boolean;
  onUndo?: () => void;
  onRedo?: () => void;
  /** Currently active annotation tool, or null when none selected. */
  annotationTool?: string | null;
  /** Called when the user selects or deselects an annotation tool. */
  onAnnotationToolChange?: (tool: string | null) => void;
  /** When true the annotation toolbar buttons are disabled. */
  annotationDisabled?: boolean;
  /** Active field tool (mirrors tools dock wiring). */
  currentTool?: FieldToolId | 'image' | null;
  /** Called when the user picks a field tool from the topbar rail (tablet). */
  onToolChange?: (tool: FieldToolId | 'image') => void;
  /** When true the field-tools rail buttons are disabled. */
  toolsDisabled?: boolean;
}

export const CommandBar: React.FC<CommandBarProps> = ({
  currentFileName,
  isBusy,
  isPlacing,
  canPlace,
  hasPdf,
  hasFields,
  canDownload,
  isSignedOutputStale,
  isDownloading = false,
  onFileSelected,
  onUploadError,
  onCreateDrawSignature,
  onCreateTypeSignature,
  onCreateUploadSignature,
  onPlacePendingSignature,
  onApply,
  onDownload,
  onCleanup,
  onBusinessPreset,
  onSaveSnapshot,
  panMode,
  onTogglePan,
  onToggleInspector,
  inspectorOpen = false,
  isUploading = false,
  isSigning = false,
  uploadProgress = 0,
  signingProgress = 0,
  isCancelling = false,
  onCancelUpload,
  onCancelSigning,
  fileSize: _fileSize = 0,
  canUndo = false,
  canRedo = false,
  onUndo,
  onRedo,
  annotationTool = null,
  onAnnotationToolChange,
  annotationDisabled = false,
  currentTool = 'select',
  onToolChange,
  toolsDisabled = false,
}) => {
  const [isDark, setIsDark] = useState(() => resolveTheme() === 'dark');

  useEffect(() => {
    const theme = resolveTheme();
    applyThemeClass(theme);
    setIsDark(theme === 'dark');
  }, []);

  const toggleDarkMode = () => {
    const nextTheme = isDark ? 'light' : 'dark';
    persistTheme(nextTheme);
    setIsDark(nextTheme === 'dark');
  };

  return (
    <header className="editor-topbar">
      <div className="editor-brand">
        <span className="editor-title">signpdf</span>
        <span className="editor-local-badge">100% local</span>
      </div>

      <PdfUploader
        onFileSelected={onFileSelected}
        onError={onUploadError}
        currentFileName={currentFileName}
        disabled={isBusy}
      />

      {(isUploading || isSigning || uploadProgress > 0 || signingProgress > 0) && (
        <div className="editor-progress" role="status" aria-live="polite">
          <div className="editor-progress-track">
            <div
              className="editor-progress-fill"
              style={{ width: `${Math.max(2, uploadProgress || signingProgress)}%` }}
            />
          </div>
          <span className="editor-progress-label">
            {isUploading
              ? uploadProgress > 0
                ? `Uploading ${uploadProgress}%`
                : 'Uploading…'
              : `Signing ${signingProgress}%`}
          </span>
          <button
            type="button"
            className="editor-btn editor-btn-danger editor-btn-sm"
            disabled={isCancelling}
            aria-label={isUploading ? 'Cancel upload' : 'Cancel signing'}
            onClick={() => {
              if (isUploading) onCancelUpload?.();
              else if (isSigning) onCancelSigning?.();
            }}
          >
            {isCancelling ? 'Cancelling…' : 'Cancel'}
          </button>
        </div>
      )}

      {/* Inspector toggle: only visible below the md breakpoint (<768px).
          The right inspector is a slide-over drawer at this width and
          needs a clear affordance. The `md:hidden` Tailwind class
          complements the .editor-inspector CSS that positions the
          drawer at the same breakpoint. The icon swaps between the
          closed state (panel-right — drawer is on the right) and the
          open state (X — drawer is open, click to close) so the user
          has a visible cue of the toggle's effect. */}
      {onToggleInspector && (
        <button
          type="button"
          onClick={onToggleInspector}
          className="editor-btn editor-btn-icon md:hidden"
          aria-label={inspectorOpen ? 'Close inspector' : 'Open inspector'}
          aria-expanded={inspectorOpen}
          aria-controls="editor-inspector"
          title="Inspector"
        >
          {inspectorOpen ? (
            // X icon — drawer is open, click closes it
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="6" y1="18" x2="18" y2="6" />
            </svg>
          ) : (
            // Panel-right icon — drawer is on the right, click opens it
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="4" width="18" height="16" rx="2" />
              <line x1="15" y1="4" x2="15" y2="20" />
            </svg>
          )}
        </button>
      )}

      <div className="editor-topbar-actions">
        {onToolChange && (
          <div className="editor-topbar-tools-rail" role="group" aria-label="Field tools">
            <ToolsIconRail
              activeTool={toolsDisabled ? null : currentTool}
              onToolChange={onToolChange}
              disabled={toolsDisabled}
            />
          </div>
        )}

        <div className="editor-topbar-group" role="group" aria-label="History">
          {onUndo && (
            <button
              type="button"
              className="editor-btn editor-btn-ghost editor-btn-sm"
              disabled={!canUndo || isBusy}
              onClick={onUndo}
              title="Undo (Ctrl+Z)"
            >
              Undo
            </button>
          )}
          {onRedo && (
            <button
              type="button"
              className="editor-btn editor-btn-ghost editor-btn-sm"
              disabled={!canRedo || isBusy}
              onClick={onRedo}
              title="Redo (Ctrl+Y)"
            >
              Redo
            </button>
          )}
        </div>

        {onAnnotationToolChange && (
          <div className="editor-topbar-group" role="group" aria-label="Annotate">
            <AnnotationToolbar
              activeTool={annotationDisabled ? null : annotationTool}
              onSelectTool={(tool) => {
                if (annotationDisabled) return;
                onAnnotationToolChange(tool);
              }}
            />
          </div>
        )}

        <div className="editor-topbar-group" role="group" aria-label="Presets">
          <button
            type="button"
            className="editor-btn editor-btn-ghost editor-btn-sm"
            disabled={!hasPdf || isBusy}
            onClick={onBusinessPreset}
          >
            Business preset
          </button>

          {onSaveSnapshot && (
            <button
              type="button"
              className="editor-btn editor-btn-ghost editor-btn-sm"
              disabled={!hasPdf || isBusy}
              onClick={onSaveSnapshot}
            >
              Snapshot
            </button>
          )}
        </div>

        <div className="editor-topbar-group" role="group" aria-label="Signature">
          <div className="editor-segment">
            <button type="button" disabled={isBusy} onClick={onCreateDrawSignature} title="Draw a signature">
              Draw
            </button>
            <button type="button" disabled={isBusy} onClick={onCreateTypeSignature} title="Type a signature">
              Type
            </button>
            <button type="button" disabled={isBusy} onClick={onCreateUploadSignature} title="Upload signature image">
              Upload
            </button>
          </div>

          <button
            type="button"
            onClick={onPlacePendingSignature}
            disabled={!canPlace || isBusy}
            className={`editor-btn editor-btn-place ${isPlacing ? 'active' : ''}`}
          >
            {isPlacing ? 'Placing…' : 'Place'}
          </button>
        </div>

        {onTogglePan && (
          <button
            type="button"
            onClick={onTogglePan}
            disabled={!hasPdf || isBusy}
            className={`editor-btn editor-btn-ghost editor-btn-sm ${panMode ? 'active' : ''}`}
            aria-pressed={!!panMode}
          >
            Pan
          </button>
        )}
      </div>

      <div className="editor-topbar-spacer" />

      {isSignedOutputStale && <span className="editor-stale-badge">Reapply needed</span>}

      <button
        type="button"
        onClick={toggleDarkMode}
        className="editor-btn editor-btn-icon"
        title={isDark ? 'Light mode' : 'Dark mode'}
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {isDark ? <Sun size={18} strokeWidth={1.75} /> : <Moon size={18} strokeWidth={1.75} />}
      </button>

      <button
        type="button"
        onClick={onApply}
        disabled={!hasFields || isBusy}
        className={`editor-btn editor-btn-apply ${hasFields && !isBusy ? 'editor-btn-apply-ready' : ''}`}
        title={
          !hasFields
            ? 'Place at least one field on the PDF first'
            : isBusy
              ? 'Please wait…'
              : 'Bake fields and signatures into the PDF'
        }
      >
        Apply to PDF
      </button>
      <button
        type="button"
        onClick={onDownload}
        disabled={!canDownload || isSignedOutputStale || isDownloading || isSigning}
        className={`editor-btn editor-btn-download ${canDownload && !isSignedOutputStale ? 'editor-btn-download-ready' : ''}`}
        title={
          isSignedOutputStale
            ? 'Fields changed — apply again before downloading'
            : !canDownload
              ? 'Apply to PDF first, then download'
              : 'Save the signed PDF to your computer'
        }
      >
        {isDownloading ? 'Saving…' : 'Download'}
      </button>
      {/* Fix A11Y-4: disable cleanup button while isBusy so destructive cleanup
           can't run during in-flight upload/signing operations. */}
      <button
        type="button"
        onClick={onCleanup}
        disabled={isBusy}
        className="editor-btn editor-btn-icon editor-btn-danger-subtle"
        title={isBusy ? 'Please wait…' : 'Clear temporary files'}
        aria-label="Clear temporary files"
      >
        <Trash2 size={16} strokeWidth={1.75} />
      </button>
    </header>
  );
};